import { forwardRef, default as React, ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import { DateRange, DateRangeInner, toUTCWithLocalTime } from "~/lib/date";
import { addDays, endOfDay, format, isSameDay, startOfTomorrow } from "date-fns";
import style from "./index.module.scss";

export const defaultDateFormat = "MM/dd/yyyy, HH:mm";

interface IRangeSelect {
  label: string;
  onChoose: () => DateRangeInner;
}

export interface IDateRangePickerProps {
  dateRange?: DateRange;
  onChange: (dateRange: DateRange) => void;
  placeholder?: ReactNode;
  disabled?: boolean;
  ranges?: IRangeSelect[];
  className?: string;
  allowSameDay?: boolean;
  allowPartial?: boolean;
  allowFutureDates?: boolean;
  minDate?: Date;
  maxDate?: Date;
  dateFormat?: string;
  fullWidth?: boolean;
  mode?: "range" | "end-only";
}

interface IDateInPicker {
  placeholder?: ReactNode;
  date?: string;
  className?: string;
}

interface ICustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  startDateText?: string;
  endDateText?: string;
  isEndOnly?: boolean;
}

const DateInPickerInput = ({ placeholder, date, className }: IDateInPicker) =>
  date ? (
    <span className={[style.dateValue, className].filter(Boolean).join(" ")}>{date}</span>
  ) : (
    <span className={[style.dateValue, style.datePlaceholder].join(" ")}>{placeholder || "empty"}</span>
  );

const CustomInput = forwardRef<HTMLInputElement, ICustomInputProps>(({ startDateText, endDateText, isEndOnly, ...props }, ref) => {
  return (
    <div className={style.datePickerRange}>
      <input ref={ref} {...props} className={style.maskedInput} />
      <i className="far fa-calendar" />
      <DateInPickerInput
        placeholder={props.placeholder}
        date={startDateText}
        className={isEndOnly ? style.lockedStartValue : undefined}
      />
      <i className="fas fa-arrow-right" />
      <DateInPickerInput placeholder={props.placeholder} date={endDateText} />
    </div>
  );
});

export const DateRangePickerInput: React.FC<IDateRangePickerProps> = ({
  dateRange,
  allowSameDay = true,
  ranges,
  disabled,
  onChange,
  allowPartial,
  allowFutureDates = false,
  minDate,
  maxDate,
  dateFormat = defaultDateFormat,
  fullWidth = false,
  className,
  mode = "range",
}) => {
  const { startDate, endDate } = dateRange ?? {};
  const initialRender = useRef(true);
  const isEndOnly = mode === "end-only";
  const startDateText = useMemo(
    () => (startDate ? format(startDate, dateFormat) : undefined),
    [dateFormat, startDate]
  );
  const endDateText = useMemo(
    () => (endDate ? format(endDate, dateFormat) : undefined),
    [dateFormat, endDate]
  );

  const handleChange = useCallback((date: DateRangeInner) => {
    if (allowPartial) {
      onChange({
        startDate: date[0] ?? undefined,
        endDate: date[1] ?? undefined,
      });
      return;
    }

    if (date[0] && date[1] && allowSameDay) {
      onChange({
        startDate: date[0] ?? undefined,
        endDate: endOfDay(date[1]) ?? undefined,
      });
    } else if (date[0]?.getTime() !== date[1]?.getTime()) {
      onChange({
        startDate: date[0] ?? undefined,
        endDate: date[1] ?? undefined,
      });
    }
  }, [allowPartial, allowSameDay, onChange]);

  const handleEndOnlyChange = useCallback((date: Date | null) => {
    onChange({
      startDate,
      endDate: date ?? undefined,
    });
  }, [onChange, startDate]);

  // need this to properly handle initial date range, cause it's treated as utc while not being utc
  useEffect(() => {
    if (initialRender.current) {
      if (isEndOnly) {
        onChange({ startDate, endDate });
      } else {
        handleChange([startDate, endDate]);
      }
      initialRender.current = false;
    }
  }, [dateRange, endDate, handleChange, isEndOnly, onChange, startDate]);

  const handleClose = () => {
    if (!isEndOnly && !endDate && startDate && !allowPartial) {
      onChange({
        startDate,
        endDate: allowSameDay ? toUTCWithLocalTime(endOfDay(startDate)) : toUTCWithLocalTime(addDays(startDate, 1)),
      });
    }
  };

  return (
    <DatePicker
      className={style.datePickerInput}
      selectsRange={!isEndOnly}
      selectsEnd={isEndOnly}
      monthsShown={2}
      selected={isEndOnly ? endDate : undefined}
      startDate={startDate}
      endDate={endDate}
      onChange={isEndOnly ? handleEndOnlyChange : handleChange}
      allowSameDay={allowSameDay}
      shouldCloseOnSelect={isEndOnly}
      strictParsing
      customInput={<CustomInput startDateText={startDateText} endDateText={endDateText} isEndOnly={isEndOnly} />}
      dateFormat={dateFormat}
      disabledKeyboardNavigation
      disabled={disabled}
      minDate={minDate}
      maxDate={maxDate}
      onCalendarClose={handleClose}
      dayClassName={(date) =>
        isEndOnly && startDate && isSameDay(date, startDate) ? style.lockedStartDay : undefined
      }
      wrapperClassName={[
        fullWidth && style.datePickerWrapperFull,
        className
      ].filter(Boolean).join(" ") || undefined}
      excludeDateIntervals={allowFutureDates
        ? undefined
        : [
          {
            start: startOfTomorrow(),
            end: new Date(10 ** 8 * 24 * 60 * 60 * 1000),
          },
        ]}
      // Needs to be disabled to prevent wrapping elements to the right
      enableTabLoop={false}
    >
      {!!ranges && (
        <div className={style.dateRanges}>
          {ranges.map((fastRange, index) => (
            <button
              key={index}
              className={style.dateRange}
              onClick={() => {
                handleChange(fastRange.onChoose());
              }}
            >
              {fastRange.label}
            </button>
          ))}
        </div>
      )}
    </DatePicker>
  );
};
